import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  Input,
  signal,
  computed,
  inject,
  effect,
  input,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { TenantMapping, TenantMappingService } from './services/tenant-mapping.service';
import { plusIcon, SVGIcon, pencilIcon, trashIcon } from '@progress/kendo-svg-icons';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { IconsModule } from '@progress/kendo-angular-icons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { LabelModule } from '@progress/kendo-angular-label';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { CyflareAutocompleteComponent } from 'src/app/core/components/cyflare-autocomplete/cyflare-autocomplete.component';

interface Company {
  id: string;
  name: string;
  selected?: boolean;
}

interface MappedCompany {
  internalId: string;
  internalName: string;
  externalId: string;
  externalName: string;
}

@Component({
  selector: 'app-tenant-mapping',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputsModule,
    TextBoxModule,
    DropDownsModule,
    ReactiveFormsModule,
    IconsModule,
    DialogModule,
    DatatableComponent,
    LabelModule,
    LoaderModule,
    CyflareAutocompleteComponent,
  ],
  templateUrl: './tenant-mapping.component.html',
  styleUrls: ['./tenant-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TenantMappingService],
})
export class TenantMappingComponent extends BaseComponent implements OnInit {
  @ViewChild('datatable') datatable!: DatatableComponent;
  @Output() close = new EventEmitter<void>();

  organizationId = input<number | null>(null);

  // Icons
  public plusIcon: SVGIcon = plusIcon;
  public pencilIcon: SVGIcon = pencilIcon;
  public trashIcon: SVGIcon = trashIcon;

  // Dialog state
  public isDialogOpen = signal<boolean>(false);
  public isDeleteDialogOpen = signal<boolean>(false);
  public dialogMode: 'add' | 'edit' = 'add';
  public selectedMapping: TenantMapping | null = null;
  public mappingToDelete: TenantMapping | null = null;

  // Warning state
  showConfigurationWarning = false;

  // Services
  private formBuilder = inject(FormBuilder);
  private tenantMappingService = inject(TenantMappingService);
  // Autocomplete state
  selectedCompanyId = signal<string | null>(null);
  savedOrgItem = signal<any>(null);
  savedCompanyItem = signal<any>(null);
  savedAgreementItem = signal<any>(null);

  agreementBaseUrl = computed(() => {
    const companyId = this.selectedCompanyId();
    if (companyId) {
      return `v1/ticketintegration/administrative/organizations/{orgId}/connectwise_agreements/?company=${companyId}`;
    }
    return `v1/ticketintegration/administrative/organizations/{orgId}/connectwise_agreements/`;
  });

  // Form
  formGroup!: FormGroup;

  // Datatable configuration
  public datatableUrl = signal<string>('');
  public datatableColumns = signal<any[]>([]);
  public datatableFilters: any = {};
  public datatableSuccessCallback: any = null;
  private currentMappings: TenantMapping[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {
    super();
    this.initEffect();
  }

  ngOnInit(): void {
    this.formGroup = this.createTenantMappingForm(new TenantMapping());
  }

  private initEffect() {
    effect(
      () => {
        const orgId = this.organizationId();
        // Only load data if we have a valid organization ID
        // null organizationId means the integration hasn't been saved yet
        if (orgId && orgId > 0) {
          // Configure datatable URL and columns
          this.datatableUrl.set(this.tenantMappingService.url);
          this.datatableFilters = { primary_organization_id: orgId };
          this.initializeDatatableColumns();

          // Company and agreement autocompletes are now handled by CyflareAutocompleteComponent
          // via template inputs (baseUrl, organizationId, etc.)
        } else if (orgId === null) {
          // Integration not saved yet - show warning
          this.showConfigurationWarning = true;
          this.cdr.markForCheck();
        }
      },
      { allowSignalWrites: true }
    );
  }

  private renderActionsColumn = (data: any, type: any, row: TenantMapping) => {
    // Store the row data for later retrieval
    this.currentMappings = this.currentMappings.filter((m) => m.id !== row.id);
    this.currentMappings.push(row);

    return `
      <div class="action-buttons d-flex gap-2">
        <button class="btn btn-sm btn-primary btn-edit d-flex align-items-center gap-1" data-id="${row.id}" title="Edit" style="max-height: 32px;">
          <span>✏️</span>
          <span>Edit</span>
        </button>
        <button class="btn btn-sm btn-danger btn-delete d-flex align-items-center gap-1" data-id="${row.id}" title="Delete" style="max-height: 32px;">
          <span>🗑️</span>
          <span>Delete</span>
        </button>
      </div>
    `;
  };

  private initializeDatatableColumns(): void {
    // Configure datatable columns
    this.datatableColumns.set([
      { data: 'organization_name', name: 'organization_name', title: 'Organization', type: 'string', visible: true },
      { data: 'company_name', name: 'company_name', title: 'Company', type: 'string', visible: true },
      { data: 'agreement_name', name: 'agreement_name', title: 'Agreement', type: 'string', visible: true },
      {
        data: 'has_problems',
        name: 'has_problems',
        title: 'Status',
        type: 'boolean',
        visible: true,
        render: (data: any) =>
          data
            ? '<span class="text-red" title="Has problems">⚠️</span>'
            : '<span class="step-number" title="No problems">✓</span>',
      },
      {
        data: null,
        name: 'actions',
        title: 'Actions',
        type: 'actions',
        orderable: false,
        visible: true,
        render: this.renderActionsColumn,
      },
    ]);
  }

  // Dialog Methods
  public openAddDialog(): void {
    this.dialogMode = 'add';
    this.selectedMapping = null;
    this.formGroup = this.createTenantMappingForm(new TenantMapping());
    this.savedOrgItem.set(null);
    this.savedCompanyItem.set(null);
    this.savedAgreementItem.set(null);
    this.selectedCompanyId.set(null);
    this.isDialogOpen.set(true);
  }

  public openEditDialog(mapping: TenantMapping): void {
    this.dialogMode = 'edit';
    this.selectedMapping = mapping;
    this.formGroup = this.createTenantMappingForm(mapping);

    // Set default values in autocompletes for edit mode
    if (mapping.organization_id && mapping.organization_name) {
      this.savedOrgItem.set({ id: mapping.organization_id, display_text: mapping.organization_name });
    }

    if (mapping.company_id && mapping.company_name) {
      this.savedCompanyItem.set({ id: mapping.company_id, display_text: mapping.company_name });
      this.selectedCompanyId.set(String(mapping.company_id));
    }

    if (mapping.agreement_id && mapping.agreement_name) {
      this.savedAgreementItem.set({ id: mapping.agreement_id, display_text: mapping.agreement_name });
    }

    this.isDialogOpen.set(true);
  }

  public closeDialog(): void {
    this.isDialogOpen.set(false);
    this.selectedMapping = null;
    this.savedOrgItem.set(null);
    this.savedCompanyItem.set(null);
    this.savedAgreementItem.set(null);
    this.selectedCompanyId.set(null);
    this.formGroup = this.createTenantMappingForm(new TenantMapping());
  }

  public saveMapping(): void {
    if (!this.formGroup || !this.formGroup.valid) {
      this.showNotification('Please fill in all required fields', 'warning');
      return;
    }

    this.tenantMappingService.upsert(this.formGroup.value).subscribe({
      next: () => {
        this.showNotification(
          this.dialogMode === 'add' ? 'Tenant mapping created successfully' : 'Tenant mapping updated successfully',
          'success'
        );
        this.closeDialog();
        this.reloadDatatable();
      },
      error: () => {
        this.showNotification('Failed to save tenant mapping', 'error');
      },
    });
  }

  public openDeleteDialog(mapping: TenantMapping): void {
    this.mappingToDelete = mapping;
    this.isDeleteDialogOpen.set(true);
  }

  public closeDeleteDialog(): void {
    this.isDeleteDialogOpen.set(false);
    this.mappingToDelete = null;
  }

  public confirmDelete(): void {
    if (!this.mappingToDelete?.id) {
      return;
    }

    this.tenantMappingService.delete(this.mappingToDelete.id).subscribe({
      next: () => {
        this.showNotification('Tenant mapping deleted successfully', 'success');
        this.closeDeleteDialog();
        this.reloadDatatable();
      },
      error: (error) => {
        this.showNotification('Failed to delete tenant mapping', 'error');
      },
    });
  }

  private reloadDatatable(): void {
    // Reload the datatable after CRUD operations
    // Use setTimeout to ensure the datatable is ready after Angular's change detection
    setTimeout(() => {
      if (this.datatable && this.datatable.reload) {
        this.datatable.reload();
      }
    }, 100);
  }

  closeMapping(): void {
    this.close.emit();
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      cssClass: `notification-${type}`,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
      closable: false,
      hideAfter: 3000,
    });
  }

  public createTenantMappingForm(tenantMapping: TenantMapping): FormGroup {
    this.formGroup = this.formBuilder.group({
      id: tenantMapping.id,
      primary_organization_id: this.organizationId(),
      organization_id: [tenantMapping.organization_id, Validators.required],
      organization_name: tenantMapping.organization_name,
      company_id: [tenantMapping.company_id, Validators.required],
      company_name: tenantMapping.company_name,
      agreement_id: [tenantMapping.agreement_id],
      agreement_name: tenantMapping.agreement_name,
    });

    // Disable agreement field if no company is selected
    if (!tenantMapping.company_id) {
      this.formGroup.get('agreement_name')?.disable();
    }

    return this.formGroup;
  }

  onOrgDropdownChanged($event: any) {
    if (!$event) return;
    this.formGroup?.patchValue({ organization_id: $event.id, organization_name: $event.display_text });

    // Clear company and agreement selections when org changes
    // Company/agreement autocompletes use the PARENT org ID (this.organizationId()) which doesn't change,
    // but we still need to clear the user's selections when they switch child orgs.
    this.savedCompanyItem.set(null);
    this.savedAgreementItem.set(null);
    this.selectedCompanyId.set(null);
    this.formGroup.patchValue({
      company_id: null,
      company_name: '',
      agreement_id: null,
      agreement_name: '',
    });
  }

  onCompanySelected(company: any): void {
    this.savedCompanyItem.set(company);
    this.selectedCompanyId.set(String(company.id));
    this.formGroup.patchValue({
      company_id: company.id,
      company_name: company.display_text,
    });

    // Enable agreement field now that a company is selected
    this.formGroup.get('agreement_name')?.enable();

    // Clear agreement selection (agreement URL will auto-update via agreementBaseUrl computed)
    this.savedAgreementItem.set(null);
    this.formGroup.patchValue({
      agreement_id: null,
      agreement_name: '',
    });
  }

  onCompanyCleared(): void {
    this.savedCompanyItem.set(null);
    this.selectedCompanyId.set(null);
    this.savedAgreementItem.set(null);
    this.formGroup.patchValue({
      company_id: null,
      company_name: '',
      agreement_id: null,
      agreement_name: '',
    });
    this.formGroup.get('agreement_name')?.disable();
  }

  onAgreementSelected(agreement: any): void {
    this.savedAgreementItem.set(agreement);
    this.formGroup.patchValue({
      agreement_id: agreement.id,
      agreement_name: agreement.display_text,
    });
  }

  onAgreementCleared(): void {
    this.savedAgreementItem.set(null);
    this.formGroup.patchValue({
      agreement_id: null,
      agreement_name: '',
    });
  }

  // Handle datatable row action clicks
  public handleDatatableClick(event: any): void {
    const target = event.target as HTMLElement;
    const button = target.closest('button');

    if (!button) return;

    const mappingId = button.getAttribute('data-id');
    if (!mappingId) return;

    // Find the mapping from the current data
    const mapping = this.findMappingById(Number(mappingId));
    if (!mapping) return;

    if (button.classList.contains('btn-edit')) {
      this.openEditDialog(mapping);
    } else if (button.classList.contains('btn-delete')) {
      this.openDeleteDialog(mapping);
    }
  }

  private findMappingById(id: number): TenantMapping | null {
    return this.currentMappings.find((m) => m.id === id) || null;
  }
}
